/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'

type UnderAgeErrorProps = {
  /** Return to the Welcome step (step 0), keeping the user's prior selections. */
  onBack?: () => void
  /** Hide the "HEADS UP!" title — used when rendered inside a card that already
   *  carries the invite header. Defaults to showing it. */
  hideHeader?: boolean
  /** Width of the Back button column. Defaults to '25%' (original signup flow);
   *  pass '50%' to match the other steps in the logged-in invite card. */
  backWidth?: string
}

/**
 * UnderAgeError component for the underage invitation flow
 * Displays a message when a user is under 13 years old
 *
 * @returns {JSX.Element} The rendered component
 */
const UnderAgeError = ({
  onBack,
  hideHeader = false,
  backWidth = '25%',
}: UnderAgeErrorProps) => {
  return (
    <div css={[containerStyle, hideHeader && noTopPadStyle]}>
      {!hideHeader && (
        <Text variant='displaySmall' weight='bold' color={colors.NEUTRAL_0}>
          HEADS UP!
        </Text>
      )}
      <div css={hideHeader ? undefined : messageStyle}>
        <Text
          variant='paragraphMedium'
          weight='regular'
          color={colors.NEUTRAL_0}
        >
          Seems like you&apos;re not 13 yet. <br />
          <br /> Please have a parent or guardian create your SportNinja
          account. Thanks!
        </Text>
      </div>
      {onBack && (
        <div css={footerStyle}>
          <div css={[backColumnStyle, css({ width: backWidth })]}>
            <Button text='Back' variant='secondary' onClick={onBack} />
          </div>
          <div css={spacerColumnStyle} />
        </div>
      )}
    </div>
  )
}

export default UnderAgeError

// ---- styled ----
const containerStyle = css`
  padding-top: 32px;
  width: 100%;
  @media (max-width: 768px) {
    width: 100%;
  }
`

// When rendered inside a card that already shows the invite header, drop the
// top padding so the message sits directly under the card divider.
const noTopPadStyle = css`
  padding-top: 0;
`

const messageStyle = css`
  margin-top: 32px;
`

const footerStyle = css`
  display: flex;
  width: 100%;
  margin-top: 32px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

// Width is set per-call via the backWidth prop (default '25%'); the spacer takes
// the remaining space so the original signup-flow layout is preserved.
const backColumnStyle = css`
  display: flex;
  @media (max-width: 768px) {
    width: 100% !important;
  }
`

const spacerColumnStyle = css`
  display: flex;
  flex: 1;
  @media (max-width: 768px) {
    width: 100%;
  }
`
